'use client';

import { useEffect, useState } from 'react';

import { DocumentImageSize } from '@/lib/types';
import { DocumentTypeCode } from '@/apiClient';
import { useGetImageByEntityId } from '@/hooks/useGetImageByEntityId';

import { Avatar, type AvatarProps, type BoxProps } from '@/primitives';

function isBlob(value: unknown): value is Blob {
  return value instanceof Blob;
}

export interface DocumentAvatarProps extends Omit<AvatarProps, 'src'> {
  documentTypeCode: DocumentTypeCode;
  documentEntityId: string | undefined;
  documentImageSize?: DocumentImageSize;
  fallbackImage?: string;
  sx?: BoxProps['sx'];
}

export function DocumentAvatar({
  documentEntityId,
  documentTypeCode,
  documentImageSize = DocumentImageSize.s,
  fallbackImage,
  ...props
}: DocumentAvatarProps) {
  const [srcData, setSrcData] = useState(fallbackImage);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  const { data: response } = useGetImageByEntityId(
    documentEntityId,
    documentTypeCode,
    documentImageSize,
  );

  useEffect(() => {
    const data = response?.data;
    if (isBlob(data) && data.size > 0) {
      const objectUrl = URL.createObjectURL(data);
      setObjectUrl(objectUrl);
    }
  }, [response]);

  if (objectUrl) {
    return <Avatar {...props} src={objectUrl} />;
  } else {
    return <Avatar {...props} src={srcData} />;
  }
}
